import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ErrorRepository} from 'angular-error-repository';
import {Apollo} from 'apollo-angular';
import gql from 'graphql-tag';
import {
    interval,
    Observable,
    ReplaySubject,
} from 'rxjs';
import {
    catchError,
    concatMap,
    concatMapTo,
    filter,
    finalize,
    map,
    takeUntil,
    takeWhile,
    tap,
} from 'rxjs/operators';

import {
    apolloGraphqlErrorHandlerSingle,
    GraphqlErrors,
} from '../../grapqhl/graphql-error-handler';
import {isNotNull} from '../../shared/utils/guards.util';
import {Logger} from '../../shared/utils/logger.util';

@Component({
    selector: 'app-magic-link',
    templateUrl: './magic-link.component.html',
    styleUrls: ['./magic-link.component.scss'],
})
export class MagicLinkComponent implements OnDestroy, OnInit {

    @Output() loginSuccess = new EventEmitter<string>();
    @Output() otherMethod = new EventEmitter();

    error: string | null = null;
    errRepo: ErrorRepository;
    form: FormGroup;
    polling = false;
    submitting = false;

    private readonly destroyed = new ReplaySubject<void>(1);

    constructor(
        private readonly apollo: Apollo,
    ) {
    }

    ngOnDestroy(): void {
        this.destroyed.next();
        this.destroyed.complete();
    }

    ngOnInit(): void {
        this.form = new FormGroup({
            email: new FormControl(null, [
                Validators.required,
                Validators.email,
            ]),
        });
        this.errRepo = new ErrorRepository(this.form);
    }

    submit(): void {
        if (!this.form.valid || this.submitting) {
            return;
        }

        this.error = null;
        this.submitting = true;
        const value = this.form.value;

        this.startMagicLink(value.email).pipe(
            takeUntil(this.destroyed),
            tap(() => {
                this.polling = true;
            }),
            concatMap(token => interval(1000).pipe(
                concatMapTo(this.tryLoginMagicLink(token)),
                takeWhile(account => account === null, true),
                filter(isNotNull),
            )),
            finalize(() => {
                this.polling = false;
                this.submitting = false;
            }),
        ).subscribe(
            account => {
                this.loginSuccess.emit(account);
            },
            error => {
                if (this.error !== null) {
                    return;
                }

                this.error = Logger.handleRequestError({
                    error,
                    message: 'Error logging in with magic link',
                });
            },
        );
    }

    private startMagicLink(email: string): Observable<string> {
        return this.apollo.mutate({
            mutation: gql`
                mutation ($email: String!) {
                    requestMagicLinkLogin(email: $email)
                }
            `,
            variables: {
                email,
            },
        }).pipe(
            map(apolloGraphqlErrorHandlerSingle),
            catchError(err => {
                if (err instanceof GraphqlErrors) {
                    if (err.hasErrorCode('NotFound')) {
                        this.error = `Cannot find account`;
                    }
                }

                throw err;
            }),
        );
    }

    private tryLoginMagicLink(token: string): Observable<string | null> {
        return this.apollo.mutate({
            mutation: gql`
                mutation ($publicToken: String!) {
                    magicLinkLoginPublic(publicToken: $publicToken)
                }
            `,
            variables: {
                publicToken: token,
            },
        }).pipe(
            map(apolloGraphqlErrorHandlerSingle),
            catchError(err => {
                if (err instanceof GraphqlErrors) {
                    if (err.hasErrorCode('Expired')) {
                        this.error = `Attempt expired please login again.`;
                    }
                }

                throw err;
            }),
        );
    }
}
